import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeName = 'purple' | 'dark' | 'light';

const THEME_STORAGE_KEY = 'pro-hub-theme';
const THEME_ORDER: ThemeName[] = ['purple', 'dark', 'light'];

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const isThemeName = (value: string | null): value is ThemeName =>
  value === 'purple' || value === 'dark' || value === 'light';

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeName(stored) ? stored : 'purple';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = (next: ThemeName) => setThemeState(next);

  const toggleTheme = () => {
    setThemeState(current => {
      const nextIndex = (THEME_ORDER.indexOf(current) + 1) % THEME_ORDER.length;
      return THEME_ORDER[nextIndex];
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
